import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { logout } from '../slices/authSlice';

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithLogout = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    api.dispatch(logout());
    window.location.href = '/login';
  }

  return result;
};

// In-app notification feed for the header bell. Live updates arrive over SSE
// (see hooks/useNotificationStream.js) and are written straight into this cache,
// so the list re-renders without a refetch.
export const notificationsApi = createApi({
  reducerPath: 'notificationsApi',
  baseQuery: baseQueryWithLogout,
  tagTypes: ['Notifications'],
  endpoints: (builder) => ({
    // First page of the feed + the unread badge count.
    // Unwraps { success, data: { items, unreadCount, hasMore, nextCursor } } → data.
    // Called with no argument so the cache key stays stable — the SSE hook
    // patches this exact entry. The backend's `before` cursor is available for a
    // future full notifications page; the bell shows the newest 20.
    getNotifications: builder.query({
      query: () => '/notifications?limit=20',
      transformResponse: (response) =>
        response?.data || { items: [], unreadCount: 0, hasMore: false, nextCursor: null },
      providesTags: ['Notifications'],
    }),

    // Polling fallback for when SSE cannot connect (blocked by a proxy, etc.).
    getUnreadCount: builder.query({
      query: () => '/notifications/unread-count',
      transformResponse: (response) => response?.data?.unreadCount ?? 0,
    }),

    markNotificationRead: builder.mutation({
      query: (id) => ({ url: `/notifications/${id}/read`, method: 'PATCH' }),
      // Flip the row locally so the popover responds instantly; the SSE 'read'
      // event keeps the merchant's other tabs in step.
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          notificationsApi.util.updateQueryData('getNotifications', undefined, (draft) => {
            const row = draft.items.find((n) => n.id === id);
            if (row && !row.read) {
              row.read = true;
              row.readAt = new Date().toISOString();
              draft.unreadCount = Math.max(0, draft.unreadCount - 1);
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    markNotificationUnread: builder.mutation({
      query: (id) => ({ url: `/notifications/${id}/unread`, method: 'PATCH' }),
      // Mirror of markNotificationRead: flip it back locally so the row
      // re-highlights instantly, and let the SSE 'unread' event sync other tabs.
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          notificationsApi.util.updateQueryData('getNotifications', undefined, (draft) => {
            const row = draft.items.find((n) => n.id === id);
            if (row && row.read) {
              row.read = false;
              row.readAt = null;
              draft.unreadCount += 1;
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    markAllNotificationsRead: builder.mutation({
      query: () => ({ url: '/notifications/read-all', method: 'POST' }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          notificationsApi.util.updateQueryData('getNotifications', undefined, (draft) => {
            const now = new Date().toISOString();
            draft.items.forEach((n) => {
              if (!n.read) {
                n.read = true;
                n.readAt = now;
              }
            });
            draft.unreadCount = 0;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    deleteNotification: builder.mutation({
      query: (id) => ({ url: `/notifications/${id}`, method: 'DELETE' }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          notificationsApi.util.updateQueryData('getNotifications', undefined, (draft) => {
            const idx = draft.items.findIndex((n) => n.id === id);
            if (idx === -1) return;
            if (!draft.items[idx].read) draft.unreadCount = Math.max(0, draft.unreadCount - 1);
            draft.items.splice(idx, 1);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    clearAllNotifications: builder.mutation({
      query: () => ({ url: '/notifications', method: 'DELETE' }),
      // Empty the list optimistically. patch.undo() restores every row if the
      // request fails, so a dropped connection cannot silently eat the feed.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          notificationsApi.util.updateQueryData('getNotifications', undefined, (draft) => {
            draft.items = [];
            draft.unreadCount = 0;
            draft.hasMore = false;
            draft.nextCursor = null;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    // Single-use, 60-second ticket for the SSE URL. EventSource cannot send an
    // Authorization header, so the bearer token is exchanged for this instead.
    createStreamTicket: builder.mutation({
      query: () => ({ url: '/notifications/stream-ticket', method: 'POST' }),
      transformResponse: (response) => response?.data || null,
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkNotificationReadMutation,
  useMarkNotificationUnreadMutation,
  useMarkAllNotificationsReadMutation,
  useDeleteNotificationMutation,
  useClearAllNotificationsMutation,
  useCreateStreamTicketMutation,
} = notificationsApi;
